// src/components/layout/Navbar.jsx
import React, { useEffect, useMemo, useState } from "react";
import { CiSearch, CiShoppingCart, CiUser } from "react-icons/ci";
import { IoClose, IoMenu, IoChevronDown } from "react-icons/io5";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../../hooks/CartContext";
import useAuthUser from "../common/useAuthUser";
import logo from "../../assets/logo2.jpg";

const Navbar = () => {
  const [search, setSearch] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  
  // Dropdown state for the side menu
  const [isMobileProductOpen, setIsMobileProductOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { authUser } = useAuthUser();
  const { cart } = useCart();

  const menuItems = useMemo(
    () => [
      ...(authUser?.role === "admin" ? ["Admin"] : []),
      "Home",
      "Product",
      "Filter",
      "About",
      "Contact",
    ],
    [authUser?.role]
  );

  // Syncing the Navbar dropdown with the categoryConfig from CreateProduct.jsx
  const productCategories = {
    Consoles: [
      { id: 'ps5', title: "PS5", subtext: "PlayStation 5" },
      { id: 'ps4', title: "PS4", subtext: "PlayStation 4" },
      { id: 'switch', title: "Nintendo Switch", subtext: "Switch & Switch 2" },
      { id: 'xbox', title: "Xbox Series", subtext: "Xbox Series X|S" },
      { id: 'handhelds', title: "Steam Deck", subtext: "PC Handhelds & ROG Ally" },
    ],
    "Game CDs": [
      { id: 'games-ps5', title: "PS5 Games", subtext: "Physical PS5 Discs" },
      { id: 'games-ps4', title: "PS4 Games", subtext: "Physical PS4 Discs" },
      { id: 'games-xbox', title: "Xbox Games", subtext: "Physical Xbox Discs" },
      { id: 'digital', title: "Games", subtext: "Digital & All Games" },
    ],
    Accessories: [
      { id: 'controllers', title: "Controller", subtext: "Gamepads & Joysticks" },
      { id: 'vr', title: "VR Headsets", subtext: "Virtual Reality" },
      { id: 'accessories', title: "Accessories", subtext: "Headsets, Cables & Docks" },
    ],
    Electronics: [
      { id: 'tech', title: "Phone & Laptop", subtext: "Smartphones & PCs" },
    ]
  };

  const toPath = (item) => {
    if (item === "Home") return "/";
    if (item === "Product") return "/consoles"; 
    return `/${item.toLowerCase().replace(/\s+/g, "-")}`;
  };

  const handleSearch = () => {
    const q = search.trim();
    if (!q) return;
    navigate(`/search/${encodeURIComponent(q)}`);
    setIsSearchOpen(false);
  };

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  }, [location.pathname]);

  // Close on ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
        setIsSearchOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-100 overflow-visible">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
        
        {/* Logo & Hamburger */}
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-lg p-2 transition hover:bg-gray-50"
            onClick={() => setIsMobileMenuOpen((p) => !p)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <IoClose size={26} /> : <IoMenu size={26} />}
          </button>
          <Link to="/" className="text-xl sm:text-2xl font-bold tracking-tight">
            <img src={logo} alt="GamePort NG" className="h-20 w-48  object-cover" />
          </Link>
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-3 sm:gap-5">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-lg p-2 hover:text-red transition focus:outline-none focus:ring-2 focus:ring-red/20"
            onClick={() => setIsSearchOpen((p) => !p)}
            aria-label="Open search"
          >
            <CiSearch size={22} />
          </button>

          <Link to="/cart" className="relative inline-flex items-center">
            <CiShoppingCart size={22} className="hover:text-red transition" />
            {cart?.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-red text-white text-[10px] min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">
                {cart.length}
              </span>
            )}
          </Link>

          {authUser ? (
            <Link to="/profile" className="inline-flex items-center">
              <CiUser size={22} className="hover:text-red transition" />
            </Link>
          ) : (
            <Link
              to="/signin"
              className="hidden sm:inline-flex bg-red text-white px-4 py-2 rounded-xl text-sm font-semibold hover:opacity-90 transition"
            >
              Login / Signup
            </Link>
          )}
        </div>
      </div>

      {/* Search Bar Dropdown */}
      {isSearchOpen && (
        <div className="absolute left-0 top-full w-full bg-white border-t border-gray-100 shadow-sm">
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-4">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search products..."
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-red/20 focus:border-red"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                autoFocus
              />
              <button
                type="button"
                onClick={handleSearch}
                className="shrink-0 rounded-xl bg-red text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition"
              >
                Search
              </button>
            </div>

            {!authUser && (
              <Link
                to="/signin"
                className="sm:hidden mt-3 inline-flex w-full justify-center bg-red text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 transition"
                onClick={() => setIsSearchOpen(false)}
              >
                Login / Signup
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Slide Menu (Universal for Desktop and Mobile) */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-300 ${
          isMobileMenuOpen ? "visible" : "invisible"
        }`}
        aria-hidden={!isMobileMenuOpen}
      >
        {/* Dark Overlay */}
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
            isMobileMenuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Side Panel */}
        <div
          className={`absolute top-0 left-0 h-full w-[80vw] max-w-[320px] bg-white shadow-lg transform transition-transform duration-300 ease-in-out overflow-y-auto ${
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="px-4 py-5 border-b border-gray-100 flex items-center justify-between">
            <p className="font-bold text-lg">
              <span className="text-black">Game</span>
              <span className="text-red">Port</span>
              <span className="text-black">NG</span>
            </p>
            <button
              type="button"
              className="p-2 rounded-lg hover:bg-gray-50"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <IoClose size={24} />
            </button>
          </div>

          <nav className="px-4 py-4 space-y-2">
            {menuItems.map((item) => {
              const path = toPath(item);
              const isActive = location.pathname === path;

              if (item === "Product") {
                return (
                  <div key={item} className="block rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-3 text-base font-medium capitalize transition">
                      <Link 
                        to={path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`${isActive ? "text-red" : "text-gray-700 hover:text-red"}`}
                      >
                        {item}
                      </Link>
                      <button
                        onClick={() => setIsMobileProductOpen(!isMobileProductOpen)}
                        className={`p-1 rounded transition ${isMobileProductOpen ? "text-red" : "text-gray-500 hover:bg-gray-100"}`}
                      >
                        <IoChevronDown className={`transition-transform duration-200 ${isMobileProductOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    
                    {/* Accordion Sub-Menu */}
                    {isMobileProductOpen && (
                      <div className="bg-gray-50/50 px-4 py-3 space-y-5">
                        {Object.entries(productCategories).map(([sectionTitle, items]) => (
                          <div key={`side-${sectionTitle}`}>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 border-b border-gray-200 pb-1">
                              {sectionTitle}
                            </p>
                            <ul className="space-y-3">
                              {items.map((cat) => (
                                <li key={`side-${cat.id}`}>
                                  <Link
                                    to={`/product-category/${encodeURIComponent(cat.title)}`}
                                    onClick={() => {
                                      setIsMobileProductOpen(false);
                                      setIsMobileMenuOpen(false);
                                    }}
                                    className="block text-left w-full text-sm font-medium text-gray-700 hover:text-red"
                                  >
                                    {cat.title}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item}
                  to={path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block rounded-xl px-3 py-3 text-base font-medium capitalize transition ${
                    isActive
                      ? "text-red bg-gray-50"
                      : "text-gray-700 hover:text-red hover:bg-gray-50"
                  }`}
                >
                  {item}
                </Link>
              );
            })}
          </nav>

          {!authUser && (
            <div className="px-4 pb-6 mt-auto">
              <Link
                to="/signin"
                onClick={() => setIsMobileMenuOpen(false)}
                className="sm:hidden inline-flex w-full justify-center bg-red text-white px-4 py-3 rounded-xl text-sm font-semibold hover:opacity-90 transition"
              >
                Login / Signup
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;